// src/utils/exportChartsUtils.js
// Utilitaires d'export pour les dashboards de logs : PNG (natif ECharts ou
// capture DOM), CSV, PDF pleine page.
// ⚠️ Nom distinct de `exportUtils.js` (déjà utilisé par UsersManagement avec
// un export default) pour éviter toute collision de module.
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { showToast } from '../components/atoms/Toast/Toast';

const slug = (s) =>
  (s || 'export')
    .toString()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const triggerDownload = (href, filename) => {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/** Export PNG natif d'un graphe ECharts via son ref (getEchartsInstance().getDataURL()). */
export const exportEchartsPNG = (echartsRef, name) => {
  try {
    const instance = echartsRef?.current?.getEchartsInstance?.();
    if (!instance) throw new Error('no chart instance');
    const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    triggerDownload(url, `${slug(name)}.png`);
    showToast('Graphique exporté en PNG', 'success');
  } catch {
    showToast('Erreur export PNG', 'error');
  }
};

/** Export PNG par capture DOM — pour les panneaux non-ECharts (listes, tableaux). */
export const exportNodeAsPNG = async (node, name) => {
  if (!node) return;
  try {
    const canvas = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
    triggerDownload(canvas.toDataURL('image/png'), `${slug(name)}.png`);
    showToast('Capturé en PNG', 'success');
  } catch {
    showToast('Erreur export PNG', 'error');
  }
};

/** Export CSV générique — rows : tableau d'objets plats. */
export const exportRowsAsCSV = (rows, name) => {
  if (!rows || !rows.length) {
    showToast('Aucune donnée à exporter', 'error');
    return;
  }
  try {
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(';'),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(';')),
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${slug(name)}.csv`);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast('Export CSV téléchargé', 'success');
  } catch {
    showToast('Erreur export CSV', 'error');
  }
};

/**
 * Export PDF de la page entière : neutralise temporairement le `zoom` CSS et
 * les `overflow` internes (sinon la capture serait tronquée à la zone
 * visible / réduite par le zoom 0.8), capture, puis restaure l'état d'origine.
 */
export const exportPageAsPDF = async (wrapperNode, name) => {
  if (!wrapperNode) return;
  const restoreFns = [];
  try {
    showToast('Génération du PDF…', 'info');

    const prevZoom = wrapperNode.style.zoom;
    wrapperNode.style.zoom = '1';
    restoreFns.push(() => { wrapperNode.style.zoom = prevZoom; });

    const prevMaxHeight = wrapperNode.style.maxHeight;
    const prevOverflow  = wrapperNode.style.overflow;
    const prevHeight    = wrapperNode.style.height;
    wrapperNode.style.maxHeight = 'none';
    wrapperNode.style.height    = 'auto';
    wrapperNode.style.overflow  = 'visible';
    restoreFns.push(() => {
      wrapperNode.style.maxHeight = prevMaxHeight;
      wrapperNode.style.overflow  = prevOverflow;
      wrapperNode.style.height    = prevHeight;
    });

    const scrollNodes = wrapperNode.querySelectorAll('[class*="--scroll"], [class*="chart-card--drill"]');
    scrollNodes.forEach((n) => {
      const prev = { maxHeight: n.style.maxHeight, overflow: n.style.overflow };
      n.style.maxHeight = 'none';
      n.style.overflow  = 'visible';
      restoreFns.push(() => { n.style.maxHeight = prev.maxHeight; n.style.overflow = prev.overflow; });
    });

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const canvas = await html2canvas(wrapperNode, {
      backgroundColor: '#f8fafc',
      scale: 2,
      useCORS: true,
      windowWidth: wrapperNode.scrollWidth,
      windowHeight: wrapperNode.scrollHeight,
    });

    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW  = pageW;
    const imgH  = (canvas.height * imgW) / canvas.width;

    let heightLeft = imgH;
    let position   = 0;
    const imgData  = canvas.toDataURL('image/png');

    pdf.addImage(imgData, 'PNG', 0, position, imgW, imgH);
    heightLeft -= pageH;
    while (heightLeft > 0) {
      position = heightLeft - imgH;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgW, imgH);
      heightLeft -= pageH;
    }

    pdf.save(`${slug(name)}.pdf`);
    showToast('PDF exporté', 'success');
  } catch {
    showToast('Erreur export PDF', 'error');
  } finally {
    restoreFns.reverse().forEach((fn) => fn());
  }
};